import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import PhoneCell from "../components/PhoneCell";
import AttributionSection from "../components/AttributionSection";
import ReferralSection from "../components/ReferralSection";
import { SOURCES, STATUSES, labelFor } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ChatCircle, ClockCounterClockwise, PaperPlaneTilt, PencilSimple, Phone, User, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [waUrl, setWaUrl] = useState(null);

  const load = async () => {
    try {
      const [l, b, a, w] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get("/branches"),
        api.get("/agents"),
        api.get(`/leads/${id}/wa-link`).catch(() => ({ data: { url: null } })),
      ]);
      setLead(l.data);
      setBranches(b.data);
      setAgents(a.data);
      setWaUrl(w.data?.url || null);
      setForm({
        customer_name: l.data.customer_name,
        phone: l.data.phone,
        source: l.data.source,
        status: l.data.status,
        branch_id: l.data.branch_id,
        agent_id: l.data.agent_id,
        notes: l.data.notes || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 404) navigate("/leads");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const branchName = branches.find((b) => b.id === lead?.branch_id)?.name || "—";
  const agentName = agents.find((a) => a.id === lead?.agent_id)?.name || "—";

  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/leads/${id}/comments`, { body: comment });
      setLead(data);
      setComment("");
    } catch (err) { toast.error(formatApiError(err)); }
    setPosting(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/leads/${id}`, form);
      setLead(data);
      setEditing(false);
      toast.success("Lead updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeStatus = async (status, force = false) => {
    try {
      const url = force ? `/leads/${id}?force=true` : `/leads/${id}`;
      const { data } = await api.patch(url, { status });
      setLead(data);
      setForm((f) => ({ ...f, status }));
      toast.success(`Status → ${labelFor(STATUSES, status)}${force ? " · admin override" : ""}`);
    } catch (err) {
      const msg = formatApiError(err);
      // Detect the can't-close-early guard error
      if (msg.includes("Cannot close as") && user?.role === "admin") {
        if (confirm(`${msg}\n\nOverride and force this status? This will bypass the cadence guard.`)) {
          return changeStatus(status, true);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Loading…
      </div>
    );
  }

  // Build interleaved activity timeline: status changes + comments
  const events = [
    ...(lead.status_history || []).map((h) => ({ kind: "status", at: h.at, status: h.status })),
    ...(lead.comments || []).map((c) => ({ kind: "comment", at: c.at, body: c.body, by_name: c.by_name })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <div className="min-h-screen">
      {/* Top */}
      <div className="border-b border-border bg-white">
        <div className="px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/leads" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-foreground" data-testid="back-to-leads">
            <ArrowLeft size={14} /> Back to leads
          </Link>
          <div className="flex items-center gap-2">
            {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-wa-button"
                className="px-3 py-2 text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1FB257] rounded-sm flex items-center gap-2 transition-colors"
                title="Open WhatsApp chat with assigned agent"
              >
                <WhatsappLogo size={14} weight="fill" /> Ping on WhatsApp
              </a>
            )}
            {!editing && (
              <button onClick={() => setEditing(true)} data-testid="edit-lead-detail-button"
                className="px-3 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
                <PencilSimple size={14} /> Edit lead
              </button>
            )}
          </div>
        </div>
        <div className="px-8 pb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Lead · {lead.id.slice(0, 8)}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold leading-none" data-testid="lead-detail-name">
            {lead.customer_name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Phone size={14} />
              <PhoneCell lead={lead} onRevealed={(p) => setLead((l) => ({ ...l, phone: p, phone_masked: null }))} />
            </span>
            <span className="flex items-center gap-2 text-muted-foreground">
              <User size={14} /> {agentName}
            </span>
            <span className="text-muted-foreground">{branchName}</span>
            <span className="text-muted-foreground">{labelFor(SOURCES, lead.source)}</span>
            <StatusBadge status={lead.status} />
          </div>
        </div>

        {/* Quick status stepper */}
        <div className="px-8 pb-4 flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => changeStatus(s.id)}
              data-testid={`quick-status-${s.id}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-bold border transition-colors ${
                lead.status === s.id
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              style={lead.status === s.id ? { backgroundColor: s.color, borderColor: s.color } : { borderColor: "#E4E4E7" }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: comments + timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Add comment */}
          <div className="border border-border bg-white">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <ChatCircle size={16} />
              <h3 className="font-display text-lg font-semibold tracking-tight">Add note / comment</h3>
            </div>
            <form onSubmit={postComment} className="p-5">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What happened on this call? Be specific."
                data-testid="comment-input"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none resize-y"
              />
              <div className="flex justify-between items-center mt-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Posting as {user?.name}
                </div>
                <button type="submit" disabled={posting || !comment.trim()} data-testid="comment-submit"
                  className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 disabled:opacity-60">
                  <PaperPlaneTilt size={14} weight="fill" /> {posting ? "Posting…" : "Post"}
                </button>
              </div>
            </form>
          </div>

          {/* Activity */}
          <div className="border border-border bg-white">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <ClockCounterClockwise size={16} />
              <h3 className="font-display text-lg font-semibold tracking-tight">Activity timeline</h3>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {events.length} event{events.length !== 1 && "s"}
              </span>
            </div>
            {events.length === 0 ? (
              <div className="px-5 py-10 text-sm text-muted-foreground text-center">No activity yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {events.map((ev, i) => (
                  <li key={i} className="px-5 py-4 flex gap-4 items-start" data-testid={`timeline-event-${i}`}>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground tabular-nums w-32 shrink-0 pt-0.5">
                      {new Date(ev.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="flex-1 min-w-0">
                      {ev.kind === "status" ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Status changed to</span>
                          <StatusBadge status={ev.status} />
                        </div>
                      ) : (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-foreground/70 mb-1">
                            {ev.by_name || "Someone"} · comment
                          </div>
                          <div className="text-sm leading-relaxed whitespace-pre-wrap">{ev.body}</div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: details / edit */}
        <div className="space-y-6">
          <div className="border border-border bg-white">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold tracking-tight">Details</h3>
              {editing && (
                <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:underline">
                  Cancel
                </button>
              )}
            </div>
            {!editing ? (
              <dl className="p-5 text-sm space-y-3" data-testid="lead-detail-summary">
                <Row label="Customer">{lead.customer_name}</Row>
                <Row label="Phone">
                  <PhoneCell lead={lead} onRevealed={(p) => setLead((l) => ({ ...l, phone: p, phone_masked: null }))} />
                </Row>
                <Row label="Source">{labelFor(SOURCES, lead.source)}</Row>
                <Row label="Branch">{branchName}</Row>
                <Row label="Agent">{agentName}</Row>
                <Row label="Status"><StatusBadge status={lead.status} /></Row>
                <Row label="Attempts">
                  <span className="tabular-nums font-semibold" data-testid="lead-attempt-count">
                    {lead.attempt_count || 0}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground ml-2">
                    {(lead.attempt_count || 0) >= 4 ? "eligible to close" : `min 4 to close as lost`}
                  </span>
                </Row>
                {(lead.callyzer_reminder_date || lead.callyzer_reminder_time) && (
                  <Row label="Callyzer reminder">
                    <span data-testid="callyzer-reminder" className="font-mono text-sm">
                      {`${lead.callyzer_reminder_date || ""} ${lead.callyzer_reminder_time || ""}`.trim()}
                    </span>
                  </Row>
                )}
                {lead.callyzer_last_note && (
                  <Row label="Callyzer note">
                    <span className="text-sm">{lead.callyzer_last_note}</span>
                  </Row>
                )}
                <Row label="Created">{new Date(lead.created_at).toLocaleString()}</Row>
                <Row label="Updated">{new Date(lead.updated_at).toLocaleString()}</Row>
                {lead.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Notes</div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap border border-border bg-secondary/40 px-3 py-2">
                      {lead.notes}
                    </div>
                  </div>
                )}
              </dl>
            ) : (
              <form onSubmit={saveEdit} className="p-5 space-y-3">
                <Edit label="Customer name">
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
                </Edit>
                <Edit label="Phone">
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
                </Edit>
                <Edit label="Source">
                  <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Edit>
                <Edit label="Branch">
                  <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </Edit>
                <Edit label="Agent">
                  <select value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </Edit>
                <Edit label="Notes">
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
                </Edit>
                <button type="submit" data-testid="lead-detail-save"
                  className="w-full mt-2 px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                  Save changes
                </button>
              </form>
            )}
          </div>
          {/* Campaign attribution (utm_*, gclid, fbclid, ad ids, landing page) */}
          <AttributionSection lead={lead} />
          <ReferralSection lead={lead} onUpdate={load} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground w-20 shrink-0">{label}</dt>
      <dd className="flex-1 text-sm">{children}</dd>
    </div>
  );
}

function Edit({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
